/*!
* @indoorequal/vue-maplibre-gl v7.7.1
* (c) 2024 François de Metz <francois@2metz.fr>
* @license MIT
*/
var te = Object.defineProperty;
var oe = (e, o, r) => o in e ? te(e, o, { enumerable: !0, configurable: !0, writable: !0, value: r }) : e[o] = r;
var q = (e, o, r) => oe(e, typeof o != "symbol" ? o + "" : o, r);
import { reactive as U, defineComponent as g, markRaw as V, getCurrentInstance as $, shallowRef as H, ref as _, provide as p, watch as d, onMounted as D, onBeforeUnmount as M, h as T, nextTick as G, inject as y, createCommentVNode as h, Teleport as ae, isRef as P, warn as re } from "vue";
import { LngLat as J, Map as ne, AttributionControl as le, FullscreenControl as ue, GeolocateControl as ie, NavigationControl as se, ScaleControl as ce, LogoControl as de, Marker as me, Popup as fe } from "maplibre-gl";
const ge = Symbol("map"), O = ge, E = Symbol("isLoaded"), ye = Symbol("isInitialized"), K = ye, L = Symbol("componentId"), C = Symbol("sourceId"), x = Symbol(
  "sourceLayerRegistry"
), Y = Symbol("marker"), m = U({
  style: "https://demotiles.maplibre.org/style.json",
  center: Object.freeze([0, 0]),
  zoom: 1,
  trackResize: !1
}), ve = [
  "error",
  "load",
  "idle",
  "remove",
  "render",
  "resize",
  "webglcontextlost",
  "webglcontextrestored",
  "dataloading",
  "data",
  "tiledataloading",
  "sourcedataloading",
  "styledataloading",
  "sourcedata",
  "styledata",
  "styleimagemissing",
  "dataabort",
  "sourcedataabort",
  "boxzoomcancel",
  "boxzoomstart",
  "boxzoomend",
  "touchcancel",
  "touchmove",
  "touchend",
  "touchstart",
  "click",
  "contextmenu",
  "dblclick",
  "mousemove",
  "mouseup",
  "mousedown",
  "mouseout",
  "mouseover",
  "movestart",
  "move",
  "moveend",
  "zoomstart",
  "zoom",
  "zoomend",
  "rotatestart",
  "rotate",
  "rotateend",
  "dragstart",
  "drag",
  "dragend",
  "pitchstart",
  "pitch",
  "pitchend",
  "wheel",
  "terrain",
  "cooperativegestureprevented"
];
function pe(e, o, r, a) {
  return (t) => r.emit(a, {
    type: t.type,
    map: o,
    component: e,
    event: t
  });
}
function be(e, o) {
  const r = J.convert(e), a = J.convert(o);
  return r.lng === a.lng && r.lat === a.lat;
}
var F = /* @__PURE__ */ ((e) => (e.TOP_LEFT = "top-left", e.TOP_RIGHT = "top-right", e.BOTTOM_LEFT = "bottom-left", e.BOTTOM_RIGHT = "bottom-right", e))(F || {});
const z = Object.values(F), W = /* @__PURE__ */ new Map(), Q = Symbol("default");
function ot(e = Q) {
  let o = W.get(e);
  return o || (o = U({ isLoaded: !1, isMounted: !1, language: null }), W.set(e, o)), o;
}
function Se(e, o, r = Q) {
  var t;
  let a = W.get(r);
  return a || (a = U({ isLoaded: !1, isMounted: !1, language: null }), W.set(r, a)), a.isLoaded = ((t = o.value) == null ? void 0 : t.loaded()) || !1, a.isMounted = !1, a.component = e, a.map = o.value, a;
}
const he = g({
  name: "MglMap",
  props: {
    /**
     * Width of the map container
     */
    width: {
      type: [Number, String],
      default: "100%"
    },
    /**
     * Height of the map container
     */
    height: {
      type: [Number, String],
      default: "100%"
    },
    /**
     * If true, the gl context will be created with MSAA antialiasing, which can be useful for antialiasing custom layers. This is false by default as a performance optimization.
     */
    antialias: {
      type: Boolean,
      default: () => m.antialias
    },
    /**
     * If set, an AttributionControl will be added to the map with the provided options. To disable the attribution control, pass false. Note: showing the logo of MapLibre is not required for using MapLibre. Default Value ts compact: true, customAttribution: "MapLibre ...".
     */
    attributionControl: {
      type: [Boolean, Object],
      default: () => m.attributionControl
    },
    /**
     * The initial bearing (rotation) of the map, measured in degrees counter-clockwise from north. If bearing is not specified in the constructor options, MapLibre GL JS will look for it in the map's style object. If it is not specified in the style, either, it will default to 0. Default Value `0`
     * @model
     */
    bearing: {
      type: Number,
      default: () => m.bearing
    },
    /**
     * The threshold, measured in degrees, that determines when the map's bearing will snap to north. For example, with a bearingSnap of 7, if the user rotates the map within 7 degrees of north, the map will automatically snap to exact north. Default Value `7`
     */
    bearingSnap: {
      type: Number,
      default: () => m.bearingSnap
    },
    /**
     * The initial bounds of the map. If bounds is specified, it overrides center and zoom constructor options.
     */
    bounds: {
      type: [Array, Object],
      default: () => m.bounds
    },
    /**
     * If true, the "box zoom" interaction is enabled (see BoxZoomHandler). Default Value ts true
     */
    boxZoom: {
      type: Boolean,
      default: () => m.boxZoom
    },
    /**
     *  The initial geographical centerpoint of the map. If center is not specified in the constructor options, MapLibre GL JS will look for it in the map's style object. If it is not specified in the style, either, it will default to [0, 0] Note: MapLibre GL JS uses longitude, latitude coordinate order (as opposed to latitude, longitude) to match GeoJSON. Default Value ts [0, 0]
     * @model
     */
    center: {
      type: [Array, Object],
      default: () => m.center
    },
    /**
     * The max number of pixels a user can shift the mouse pointer during a click for it to be considered a valid click (as opposed to a mouse drag). Default Value ts true
     */
    clickTolerance: {
      type: Number,
      default: () => m.clickTolerance
    },
    /**
     * If true, Resource Timing API information will be collected for requests made by GeoJSON and Vector Tile web workers (this information is normally inaccessible from the main Javascript thread). Information will be returned in a resourceTiming property of relevant data events. Default Value `false`
     */
    collectResourceTiming: {
      type: Boolean,
      default: () => m.collectResourceTiming
    },
    /**
     * If true, symbols from multiple sources can collide with each other during collision detection. If false, collision detection is run separately for the symbols in each source. Default Value `true`
     */
    crossSourceCollisions: {
      type: Boolean,
      default: () => m.crossSourceCollisions
    },
    /**
     * If true, the "drag to pan" interaction is enabled. An Object value is passed as options to DragPanHandler#enable. Default Value `true`
     */
    dragPan: {
      type: Boolean,
      default: () => m.dragPan
    },
    /**
     * If true, the "drag to rotate" interaction is enabled (see DragRotateHandler). Default Value `true`
     */
    dragRotate: {
      type: Boolean,
      default: () => m.dragRotate
    },
    /**
     * If true, the "double click to zoom" interaction is enabled (see DoubleClickZoomHandler). Default Value `true`
     */
    doubleClickZoom: {
      type: Boolean,
      default: () => m.doubleClickZoom
    },
    /**
     * If true, the map's position (zoom, center latitude, center longitude, bearing, and pitch) will be synced with the hash fragment of the page's URL. For example, http://path/to/my/page.html#2.59/39.26/53.07/-24.1/60. An additional string may optionally be provided to indicate a parameter-styled hash, e.g. http://path/to/my/page.html#map=2.59/39.26/53.07/-24.1/60&foo=bar, where foo is a custom parameter and bar is an arbitrary hash distinct from the map hash. Default Value `false`
     */
    hash: {
      type: [Boolean, String],
      default: () => m.hash
    },
    /**
     * Controls the duration of the fade-in/fade-out animation for label collisions after initial map load, in milliseconds. This setting affects all symbol layers. This setting does not affect the duration of runtime styling transitions or raster tile cross-fading. Default Value `300`
     */
    fadeDuration: {
      type: Number,
      default: () => m.fadeDuration
    },
    /**
     * If true, map creation will fail if the performance of MapLibre GL JS would be dramatically worse than expected (i.e. a software renderer would be used). Default Value `false`
     */
    failIfMajorPerformanceCaveat: {
      type: Boolean,
      default: () => m.failIfMajorPerformanceCaveat
    },
    /**
     * A FitBoundsOptions options object to use only when fitting the initial bounds provided above.
     */
    fitBoundsOptions: {
      type: Object,
      default: () => m.fitBoundsOptions
    },
    /**
     * If false, no mouse, touch, or keyboard listeners will be attached to the map, so it will not respond to interaction. Default Value `true`
     */
    interactive: {
      type: Boolean,
      default: () => m.interactive
    },
    /**
     * If true, keyboard shortcuts are enabled (see KeyboardHandler). Default Value `true`
     */
    keyboard: {
      type: Boolean,
      default: () => m.keyboard
    },
    /**
     * A patch to apply to the default localization table for UI strings, e.g. control tooltips. The locale object maps namespaced UI string IDs to translated strings in the target language; see src/ui/default_locale.js for an example with all supported string IDs. The object may specify all UI strings (thereby adding support for a new translation) or only a subset of strings (thereby patching the default translation table). Default Value `null`
     */
    locale: {
      type: Object,
      default: () => m.locale
    },
    localIdeographFontFamily: {
      type: String,
      default: () => m.localIdeographFontFamily
    },
    /**
     * A string representing the position of the MapLibre wordmark on the map. Valid options are top-left,top-right, bottom-left, or bottom-right. Default Value 'bottom-left'
     */
    logoPosition: {
      type: [String],
      validator: (e) => e in F,
      default: () => m.logoPosition
    },
    /**
     * If set, the map will be constrained to the given bounds.
     */
    maxBounds: {
      type: [Array, Object],
      default: () => m.maxBounds
    },
    /**
     * The maximum pitch of the map (0-85). Values greater than 60 degrees are experimental and may result in rendering issues. If you encounter any, please raise an issue with details in the MapLibre project. Default Value `60`
     */
    maxPitch: {
      type: Number,
      default: () => m.maxPitch
    },
    /**
     * The maximum zoom level of the map (0-24). Default Value `22`
     */
    maxZoom: {
      type: Number,
      default: () => m.maxZoom
    },
    /**
     * The minimum pitch of the map (0-85). Values greater than 60 degrees are experimental and may result in rendering issues. If you encounter any, please raise an issue with details in the MapLibre project. Default Value `0`
     */
    minPitch: {
      type: Number,
      default: () => m.minPitch
    },
    /**
     * The minimum zoom level of the map (0-24). Default Value `0`
     */
    minZoom: {
      type: Number,
      default: () => m.minZoom
    },
    /**
     * If true, the map's canvas can be exported to a PNG using map.getCanvas().toDataURL(). This is false by default as a performance optimization. Default Value `false`
     */
    preserveDrawingBuffer: {
      type: Boolean,
      default: () => m.preserveDrawingBuffer
    },
    /**
     * The initial pitch (tilt) of the map, measured in degrees away from the plane of the screen (0-85). If pitch is not specified in the constructor options, MapLibre GL JS will look for it in the map's style object. If it is not specified in the style, either, it will default to 0. Values greater than 60 degrees are experimental and may result in rendering issues. If you encounter any, please raise an issue with details in the MapLibre project. Default Value `0`
     * @model
     */
    pitch: { type: Number, default: () => m.pitch },
    /**
     * If false, the map's pitch (tilt) control with "drag to rotate" interaction will be disabled. Default Value `true`
     */
    pitchWithRotate: {
      type: Boolean,
      default: () => m.pitchWithRotate
    },
    /**
     * If false, the map won't attempt to re-request tiles once they expire per their HTTP cacheControl/expires headers. Default Value `true`
     */
    refreshExpiredTiles: {
      type: Boolean,
      default: () => m.refreshExpiredTiles
    },
    renderWorldCopies: {
      type: Boolean,
      default: () => m.renderWorldCopies
    },
    scrollZoom: {
      type: Boolean,
      default: () => m.scrollZoom
    },
    mapStyle: {
      type: [String, Object],
      default: () => m.style
    },
    /**
     * If `true`, the map will automatically resize when the browser window resizes.
     * Default value true
     */
    trackResize: {
      type: Boolean,
      default: () => m.trackResize
    },
    /**
     * A callback run before the Map makes a request for an external URL. The callback can be used to modify the url, set headers, or set the credentials property for cross-origin requests.
     * Expected to return an object with a `url` property and optionally `headers` and `credentials` properties.
     */
    transformRequest: {
      type: Function,
      default: m.transformRequest
    },
    /**
     * A callback run before the map's camera is moved due to user input or animation. The callback can be used to modify the new center, zoom, pitch and bearing.
     * Expected to return an object containing center, zoom, pitch or bearing values to overwrite.
     * @since 6.4.0
     */
    transformCameraUpdate: {
      type: Function,
      default: m.transformCameraUpdate
    },
    /**
     * The map's TwoFingersTouchZoomRotateHandler, which allows the user to zoom or rotate the map with touch gestures.
     * Find more details and examples using `touchZoomRotate` in the TwoFingersTouchZoomRotateHandler section.
     */
    touchZoomRotate: {
      type: Boolean,
      default: () => m.touchZoomRotate
    },
    /**
     * The map's TwoFingersTouchPitchHandler, which allows the user to pitch the map with touch gestures.
     * Find more details and examples using `touchPitch` in the TwoFingersTouchPitchHandler section.
     */
    touchPitch: {
      type: Boolean,
      default: () => m.touchPitch
    },
    /**
     * The initial zoom level of the map. If zoom is not specified in the constructor options, MapLibre GL JS will look for it in the map's style object. If it is not specified in the style, either, it will default to 0. Default Value `0`
     * @model
     */
    zoom: { type: Number, default: () => m.zoom },
    /**
     * The maximum number of tiles stored in the tile cache for a given source. If omitted, the cache will be dynamically sized based on the current viewport which can be set using `maxTileCacheZoomLevels` constructor options.
     * Default value null
     */
    maxTileCacheSize: {
      type: Number,
      default: () => m.maxTileCacheSize
    },
    /**
     * The name or symbol to reference a map via useMap composable
     */
    mapKey: { type: [String, Symbol] },
    /**
     * The pixel ratio. The canvas' width attribute will be container.clientWidth * pixelRatio and its height attribute will be container.clientHeight * pixelRatio. Defaults to devicePixelRatio if not specified.
     */
    pixelRatio: {
      type: Number,
      default: () => m.pixelRatio
    },
    /**
     * If false, style validation will be skipped. Useful in production environment.
     * Default value true
     * @since 6.4.0
     */
    validateStyle: {
      type: Boolean,
      default: () => m.validateStyle
    },
    /**
     * The map's {@link CooperativeGesturesHandler}, which allows the user to see cooperative gesture info when user tries to zoom in/out.
     * Find more details and examples using `cooperativeGestures` in the {@link CooperativeGesturesHandler} section.
     */
    cooperativeGestures: {
      type: Object,
      default: () => m.cooperativeGestures
    }
  },
  emits: [
    "map:error",
    "map:load",
    "map:idle",
    "map:remove",
    "map:render",
    "map:resize",
    "map:webglcontextlost",
    "map:webglcontextrestored",
    "map:dataloading",
    "map:data",
    "map:tiledataloading",
    "map:sourcedataloading",
    "map:styledataloading",
    "map:sourcedata",
    "map:styledata",
    "map:styleimagemissing",
    "map:dataabort",
    "map:sourcedataabort",
    "map:boxzoomcancel",
    "map:boxzoomstart",
    "map:boxzoomend",
    "map:touchcancel",
    "map:touchmove",
    "map:touchend",
    "map:touchstart",
    "map:click",
    "map:contextmenu",
    "map:dblclick",
    "map:mousemove",
    "map:mouseup",
    "map:mousedown",
    "map:mouseout",
    "map:mouseover",
    "map:movestart",
    "map:move",
    "map:moveend",
    "map:zoomstart",
    "map:zoom",
    "map:zoomend",
    "map:rotatestart",
    "map:rotate",
    "map:rotateend",
    "map:dragstart",
    "map:drag",
    "map:dragend",
    "map:pitchstart",
    "map:pitch",
    "map:pitchend",
    "map:wheel",
    "map:terrain",
    "map:cooperativegestureprevented",
    /**
     * Center property updated
     */
    "update:center",
    /**
     * Zoom property updated
     */
    "update:zoom",
    /**
     * Pitch property updated
     */
    "update:pitch",
    /**
     * Bearing property updated
     */
    "update:bearing"
  ],
  slots: Object,
  setup(e, o) {
    const r = V($()), a = H(), t = H(), n = _(!1), f = _(!1), s = /* @__PURE__ */ new Map(), v = Se(r, t, e.mapKey);
    p(O, t), p(E, f), p(K, n), p(L, r.uid), p(C, ""), d(
      () => e.bearing,
      (i) => {
        var u;
        i && ((u = t.value) == null || u.setBearing(i));
      }
    ), d(
      () => e.bounds,
      (i) => {
        var u;
        i && ((u = t.value) == null || u.fitBounds(i, e.fitBoundsOptions));
      }
    ), d(
      () => e.center,
      (i) => {
        var Z, A;
        const u = (Z = t.value) == null ? void 0 : Z.getCenter();
        i && u && !be(i, u) && ((A = t.value) == null || A.setCenter(i));
      }
    ), d(
      () => e.maxBounds,
      (i) => {
        var u;
        i && ((u = t.value) == null || u.setMaxBounds(i));
      }
    ), d(
      () => e.maxPitch,
      (i) => {
        var u;
        i && ((u = t.value) == null || u.setMaxPitch(i));
      }
    ), d(
      () => e.maxZoom,
      (i) => {
        var u;
        i && ((u = t.value) == null || u.setMaxZoom(i));
      }
    ), d(
      () => e.minPitch,
      (i) => {
        var u;
        i && ((u = t.value) == null || u.setMinPitch(i));
      }
    ), d(
      () => e.minZoom,
      (i) => {
        var u;
        i && ((u = t.value) == null || u.setMinZoom(i));
      }
    ), d(
      () => e.pitch,
      (i) => {
        var u;
        i && ((u = t.value) == null || u.setPitch(i));
      }
    ), d(
      () => e.renderWorldCopies,
      (i) => {
        var u;
        i && ((u = t.value) == null || u.setRenderWorldCopies(i));
      }
    ), d(
      () => e.mapStyle,
      (i) => {
        var u;
        i && ((u = t.value) == null || u.setStyle(i));
      }
    ), d(
      () => e.transformRequest,
      (i) => {
        var u;
        i && ((u = t.value) == null || u.setTransformRequest(i));
      }
    ), d(
      () => e.zoom,
      (i) => {
        var u;
        i && ((u = t.value) == null || u.setZoom(i));
      }
    ), d(
      () => e.zoom,
      (i) => {
        var u;
        i && ((u = t.value) == null || u.setZoom(i));
      }
    );
    function l() {
      v.isMounted = !0;
      const i = { ...e, style: e.mapStyle, container: a.value };
      for (const u of Object.keys(i))
        i[u] === void 0 && delete i[u];
      if (t.value = V(new ne(i)), v.map = t.value, n.value = !0, s.set(
        "__load",
        () => (f.value = !0, v.isLoaded = !0)
      ), t.value.on("load", s.get("__load")), s.set(
        "__moveend",
        () => o.emit("update:center", t.value.getCenter())
      ), t.value.on("moveend", s.get("__moveend")), s.set(
        "__zoomend",
        () => o.emit("update:zoom", t.value.getZoom())
      ), t.value.on("zoomend", s.get("__zoomend")), s.set(
        "__pitchend",
        () => o.emit("update:pitch", t.value.getPitch())
      ), t.value.on("pitchend", s.get("__pitchend")), s.set(
        "__rotateend",
        () => o.emit("update:bearing", t.value.getBearing())
      ), t.value.on("rotateend", s.get("__rotateend")), r.vnode.props) {
        for (const u of ve)
          if (r.vnode.props["onMap:" + u]) {
            const Z = `map:${u}`, A = pe(
              r,
              t.value,
              o,
              Z
            );
            s.set(u, A), t.value.on(u, A);
          }
      }
      t.value.getCanvas().addEventListener("webglcontextlost", R);
    }
    async function c() {
      v.isMounted = !1, v.isLoaded = !1, f.value = !1, t.value && (t.value.getCanvas().removeEventListener("webglcontextlost", R), n.value = !1, s.forEach((i, u) => {
        t.value.off(u.startsWith("__") ? u.substring(2) : u, i);
      }), t.value.remove());
    }
    function R() {
      c(), G(l);
    }
    return D(l), M(c), o.expose({ map: t }), () => [
      T("div", {
        ref: a,
        style: { height: e.height, width: e.width }
      }),
      n.value && o.slots.default ? o.slots.default({}) : void 0
    ];
  },
  /**
   * Slot for controls, sources, marker and popup
   * @slot default
   */
  render() {
    return null;
  }
});
function Me(e, o, r) {
  d(
    e,
    (a) => {
      var t, n;
      a && z.indexOf(a) === -1 || ((t = o.value) != null && t.hasControl(r) && o.value.removeControl(r), (n = o.value) == null || n.addControl(r, a));
    },
    { immediate: !0 }
  );
}
function j(e, o) {
  const r = y(O), a = y(K), t = H();
  return t.value = e(), Me(() => o.position, r, t.value), M(
    () => {
      var n;
      return a.value && ((n = r.value) == null ? void 0 : n.removeControl(t.value));
    }
  ), { control: t, map: r };
}
const Le = g({
  name: "MglAttributionControl",
  props: {
    /**
     * Position on the map to which the control will be added. Valid values are 'top-left', 'top-right', 'bottom-left', and 'bottom-right'. Defaults to 'bottom-right'.
     */
    position: {
      type: String,
      validator: (e) => z.indexOf(e) !== -1
    },
    /**
     * If true, the attribution control will always collapse when moving the map. If false, force the expanded attribution control. The default is a responsive attribution that collapses when the user moves the map on maps less than 640 pixels wide. Attribution should not be collapsed if it can comfortably fit on the map. compact should only be used to modify default attribution when map size makes it impossible to fit default attribution and when the automatic compact resizing for default settings are not sufficient.
     */
    compact: Boolean,
    /**
     * Attributions to show in addition to any other attributions.
     */
    customAttribution: [String, Array]
  },
  setup(e) {
    j(() => new le({
      compact: e.compact,
      customAttribution: e.customAttribution
    }), e);
  },
  render() {
    return null;
  }
});
class Ce {
  constructor(o, r) {
    q(this, "container");
    this.isAdded = o, this.container = document.createElement("div"), this.setClasses(r);
  }
  getDefaultPosition() {
    return F.TOP_LEFT;
  }
  onAdd() {
    return G(() => this.isAdded.value = !0), this.container;
  }
  onRemove() {
    this.isAdded.value = !1, this.container.remove();
  }
  setClasses(o) {
    this.container.className = o;
  }
}
const Oe = g({
  name: "MglCustomControl",
  props: {
    /**
     * Position on the map to which the control will be added. Valid values are 'top-left', 'top-right', 'bottom-left', and 'bottom-right'. Defaults to 'top-left'.
     */
    position: {
      type: String,
      validator: (e) => z.indexOf(e) !== -1
    },
    /**
     * Set the classes on the control div
     * @since 6.1.0
     */
    class: {
      type: String,
      default: "maplibregl-ctrl maplibregl-ctrl-group"
    }
  },
  slots: Object,
  setup(e, { slots: o }) {
    const r = _(!1), { control: a } = j(() => new Ce(r, e.class), e);
    return d(
      () => e.class,
      () => a.value.setClasses(e.class)
    ), () => {
      var t;
      return r.value ? T(ae, { to: a.value.container }, (t = o.default) == null ? void 0 : t.call(o, {})) : h("custom-component");
    };
  },
  /**
   * Slot to render the content of the control
   * @slot default
   */
  render() {
    return null;
  }
}), xe = g({
  name: "MglFullscreenControl",
  props: {
    /**
     * Position on the map to which the control will be added. Valid values are 'top-left', 'top-right', 'bottom-left', and 'bottom-right'. Defaults to 'top-right'.
     */
    position: {
      type: String,
      validator: (e) => z.indexOf(e) !== -1
    },
    /**
     * container is the compatible DOM element which should be made full screen. By default, the map container element will be made full screen.
     */
    container: {
      type: Object,
      default: null
    }
  },
  setup(e) {
    const { control: o, map: r } = j(() => new ue({
      container: e.container || void 0
    }), e);
    function a() {
      G(() => {
        var t;
        return (t = r.value) == null ? void 0 : t.resize();
      });
    }
    o.value.on("fullscreenstart", a), o.value.on("fullscreenend", a), M(() => {
      o.value.off("fullscreenstart", a), o.value.off("fullscreenend", a);
    });
  },
  render() {
    return null;
  }
}), ze = g({
  name: "MglGeolocateControl",
  props: {
    /**
     * Position on the map to which the control will be added. Valid values are 'top-left', 'top-right', 'bottom-left', and 'bottom-right'. Defaults to 'top-right'.
     */
    position: {
      type: String,
      default: F.TOP_RIGHT,
      validator: (e) => z.indexOf(e) !== -1
    },
    /**
     * A Geolocation API [PositionOptions](https://developer.mozilla.org/en-US/docs/Web/API/PositionOptions) object.
     */
    positionOptions: {
      type: Object,
      default: () => ({ enableHighAccuracy: !1, timeout: 6e3 })
    },
    /**
     * A options object to use when the map is panned and zoomed to the user's location. The default is to use a maxZoom of 15 to limit how far the map will zoom in for very accurate locations.
     */
    fitBoundsOptions: {
      type: Object,
      default: () => ({ maxZoom: 15 })
    },
    /**
     * If true the GeolocateControl becomes a toggle button and when active the map will receive updates to the user's location as it changes.
     */
    trackUserLocation: {
      type: Boolean,
      default: !1
    },
    /**
     * By default, if showUserLocation is true, a transparent circle will be drawn around the user location indicating the accuracy (95% confidence level) of the user's location. Set to false to disable. Always disabled when showUserLocation is false.
     */
    showAccuracyCircle: {
      type: Boolean,
      default: !0
    },
    /**
     * By default a dot will be shown on the map at the user's location. Set to false to disable.
     */
    showUserLocation: {
      type: Boolean,
      default: !0
    }
  },
  emits: [
    "trackuserlocationstart",
    "trackuserlocationend",
    "userlocationlostfocus",
    "userlocationfocus",
    "geolocate",
    "error",
    "outofmaxbounds"
  ],
  setup(e, o) {
    const { control: r } = j(() => new ie({
      positionOptions: e.positionOptions,
      fitBoundsOptions: e.fitBoundsOptions,
      trackUserLocation: e.trackUserLocation,
      showAccuracyCircle: e.showAccuracyCircle,
      showUserLocation: e.showUserLocation
    }), e);
    function a(t) {
      const n = (f) => {
        o.emit(t, f);
      };
      r.value.on(t, n), M(() => {
        r.value.off(t, n);
      });
    }
    a("trackuserlocationstart"), a("trackuserlocationend"), a("userlocationlostfocus"), a("userlocationfocus"), a("geolocate"), a("error"), a("outofmaxbounds");
  },
  render() {
    return null;
  }
}), Be = g({
  name: "MglNavigationControl",
  props: {
    /**
     * Position on the map to which the control will be added. Valid values are 'top-left', 'top-right', 'bottom-left', and 'bottom-right'. Defaults to 'top-right'.
     */
    position: {
      type: String,
      validator: (e) => z.indexOf(e) !== -1
    },
    /**
     * If true the compass button is included.
     */
    showCompass: { type: Boolean, default: !0 },
    /**
     * If true the zoom-in and zoom-out buttons are included.
     */
    showZoom: { type: Boolean, default: !0 },
    /**
     * If true the pitch is visualized by rotating X-axis of compass.
     */
    visualizePitch: Boolean
  },
  setup(e) {
    j(() => new se({
      showCompass: e.showCompass,
      showZoom: e.showZoom,
      visualizePitch: e.visualizePitch
    }), e);
  },
  render() {
    return null;
  }
});
var X = /* @__PURE__ */ ((e) => (e.IMPERIAL = "imperial", e.METRIC = "metric", e.NAUTICAL = "nautical", e))(X || {});
const Ie = Object.values(X), Re = g({
  name: "MglScaleControl",
  props: {
    /**
     * Position on the map to which the control will be added. Valid values are 'top-left', 'top-right', 'bottom-left', and 'bottom-right'. Defaults to 'bottom-left'.
     */
    position: {
      type: String,
      validator: (e) => z.indexOf(e) !== -1
    },
    /**
     * The maximum length of the scale control in pixels. Default Value `100`
     */
    maxWidth: { type: Number, default: 100 },
    /**
     * Set the scale's unit of the distance ('imperial', 'metric' or 'nautical').
     */
    unit: {
      type: String,
      default: "metric",
      validator: (e) => Ie.indexOf(e) !== -1
    }
  },
  setup(e) {
    j(() => new ce({
      maxWidth: e.maxWidth,
      unit: e.unit
    }), e);
  },
  render() {
    return null;
  }
}), _e = g({
  name: "MglLogoControl",
  props: {
    /**
     * Position on the map to which the control will be added. Valid values are 'top-left', 'top-right', 'bottom-left', and 'bottom-right'. Defaults to 'bottom-left'.
     */
    position: {
      type: String,
      validator: (e) => z.indexOf(e) !== -1
    },
    /**
     * If true, force a compact logo. If false, force the full logo. The default is a responsive logo that collapses when the map is less than 640 pixels wide.
     */
    compact: {
      type: Boolean
    }
  },
  setup(e) {
    j(() => new de({
      compact: e.compact
    }), e);
  },
  render() {
    return null;
  }
}), je = g({
  name: "MglMarker",
  emits: [
    /**
     * Fired when dragging starts
     */
    "dragstart",
    /**
     * Fired while dragging
     */
    "drag",
    /**
     * Fired when the marker is finished being dragged
     */
    "dragend",
    /**
     * Fired when the coordinates have been updated
     *
     * @property {LgnLatLike} coordinates the new coordinates
     */
    "update:coordinates"
  ],
  props: {
    /**
     * Marker coordinates
     */
    coordinates: {
      type: [Object, Array],
      required: !0
    },
    /**
     * The offset in pixels as a PointLike object to apply relative to the element's center. Negatives indicate left and up.
     */
    offset: [Object, Array],
    /**
     * A string indicating the part of the Marker that should be positioned closest to the coordinate set via Marker#setLngLat. Options are 'center', 'top', 'bottom', 'left', 'right', 'top-left', 'top-right', 'bottom-left', and 'bottom-right'. Default Value 'center'
     */
    anchor: String,
    /**
     * The color to use for the default marker if options.element is not provided. The default is light blue. Default Value '#3FB1CE'
     */
    color: String,
    /**
     * A boolean indicating whether or not a marker is able to be dragged to a new position on the map. Default Value false
     */
    draggable: Boolean,
    /**
     * The max number of pixels a user can shift the mouse pointer during a click on the marker for it to be considered a valid click (as opposed to a marker drag). The default is to inherit map's clickTolerance. Default Value 0
     */
    clickTolerance: Number,
    /**
     * The rotation angle of the marker in degrees, relative to its respective rotationAlignment setting. A positive value will rotate the marker clockwise. Default Value 0
     */
    rotation: Number,
    /**
     * map aligns the Marker's rotation relative to the map, maintaining a bearing as the map rotates. viewport aligns the Marker's rotation relative to the viewport, agnostic to map rotations. auto is equivalent to viewport. Default Value 'auto'
     */
    rotationAlignment: String,
    /**
     * map aligns the Marker to the plane of the map. viewport aligns the Marker to the plane of the viewport. auto automatically matches the value of rotationAlignment. Default Value 'auto'
     */
    pitchAlignment: String,
    /**
     * The scale to use for the default marker if options.element is not provided. The default scale corresponds to a height of 41px and a width of 27px. Default Value 1
     */
    scale: Number,
    /**
     * Marker's opacity when it's in clear view (not behind 3d terrain). Default value `1`
     * @since 7.0.0
     */
    opacity: String,
    /**
     * Marker's opacity when it's behind 3d terrain
     * @defaultValue `0.2`
     * @since 7.0.0
     */
    opacityWhenCovered: String,
    /**
     * If true, rounding is disabled for placement of the marker, allowing for subpixel positioning and smoother movement when the marker is translated.
     * @since 7.5.0
     */
    subpixelPositioning: {
      type: Boolean,
      default: !1
    }
  },
  setup(e, { slots: o, emit: r }) {
    const a = y(O), t = H(), n = _(), f = _(!1), s = /* @__PURE__ */ new Map();
    function v(l, c) {
      const R = () => {
        c && c(), r(l);
      };
      t.value.on(l, R), s.set(l, R);
    }
    return p(Y, t), D(() => {
      const l = { ...e };
      o.marker && (l.element = n.value), t.value = new me(l), t.value.setLngLat(e.coordinates).addTo(a.value), v("dragstart"), v("drag", () => {
        var c;
        r("update:coordinates", (c = t.value) == null ? void 0 : c.getLngLat());
      }), v("dragend", () => {
        var c;
        r("update:coordinates", (c = t.value) == null ? void 0 : c.getLngLat());
      }), f.value = !0;
    }), d(
      () => e.coordinates,
      (l) => {
        var c;
        return (c = t.value) == null ? void 0 : c.setLngLat(l);
      },
      { deep: !0 }
    ), d(
      () => e.draggable,
      (l) => {
        var c;
        return (c = t.value) == null ? void 0 : c.setDraggable(l);
      }
    ), d(
      () => e.offset,
      (l) => {
        var c;
        return (c = t.value) == null ? void 0 : c.setOffset(l || [0, 0]);
      }
    ), d(
      () => e.pitchAlignment,
      (l) => {
        var c;
        return (c = t.value) == null ? void 0 : c.setPitchAlignment(l || "auto");
      }
    ), d(
      () => e.rotation,
      (l) => {
        var c;
        return (c = t.value) == null ? void 0 : c.setRotation(l);
      }
    ), d(
      () => e.rotationAlignment,
      (l) => {
        var c;
        return (c = t.value) == null ? void 0 : c.setRotationAlignment(l || "auto");
      }
    ), d(
      () => e.opacity,
      (l) => {
        var c;
        return (c = t.value) == null ? void 0 : c.setOpacity(l, e.opacityWhenCovered);
      }
    ), d(
      () => e.opacityWhenCovered,
      (l) => {
        var c;
        return (c = t.value) == null ? void 0 : c.setOpacity(e.opacity, l);
      }
    ), d(
      () => e.subpixelPositioning,
      (l) => {
        var c;
        return (c = t.value) == null ? void 0 : c.setSubpixelPositioning(l);
      }
    ), M(() => {
      var l;
      s.forEach((c, R) => {
        var i;
        (i = t.value) == null || i.off(R, c);
      }), (l = t.value) == null || l.remove();
    }), () => [
      T(
        "div",
        o.default && f.value ? o.default({}) : void 0
      ),
      T("div", { ref: n }, o.marker ? o.marker() : void 0)
    ];
  },
  /**
   * Slot for popup component
   * @slot default
   */
  /**
   * Slot for custom marker
   * @slot marker
   */
  render() {
    return null;
  }
}), Ne = g({
  name: "MglPopup",
  emits: [
    /**
     * Fired when the popup is opened manually or programmatically.
     */
    "open",
    /**
     * Fired when the popup is closed manually or programmatically.
     */
    "close"
  ],
  props: {
    /**
     * The geographical location of the popup's anchor.
     * Unused when placed inside a marker.
     */
    coordinates: {
      type: [Object, Array],
      required: !1
    },
    /**
     * Display a close button in the top right corner.
     */
    closeButton: {
      type: Boolean,
      required: !1,
      default: !0
    },
    /**
     * The popup will be closed when the map is clicked.
     */
    closeOnClick: {
      type: Boolean,
      required: !1,
      default: !0
    },
    /**
     * The popup will be closed when the map moves.
     */
    closeOnMove: {
      type: Boolean,
      required: !1,
      default: !1
    },
    /**
     * The popup will try to focus the first focusable element inside the popup.
     */
    focusAfterOpen: {
      type: Boolean,
      required: !1,
      default: !0
    },
    /**
     * A string indicating the part of the Popup that should
     * be positioned closest to the coordinate.
     * Options are `'center'`, `'top'`, `'bottom'`, `'left'`, `'right'`, `'top-left'`,
     * `'top-right'`, `'bottom-left'`, and `'bottom-right'`. If unset the anchor will be
     * dynamically set to ensure the popup falls within the map container with a preference
     * for `'bottom'`.
     */
    anchor: {
      type: String,
      required: !1
    },
    /**
     * A pixel offset applied to the popup's location
     */
    offset: {
      type: [Number, Object, Array],
      required: !1
    },
    /**
     * Space-separated CSS class names to add to popup container
     */
    className: {
      type: String,
      required: !1
    },
    /**
     * A string that sets the CSS property of the popup's maximum width, eg `'300px'`.
     * To ensure the popup resizes to fit its content, set this property to `'none'`.
     */
    maxWidth: {
      type: String,
      default: "240px"
    },
    /**
     * If true, rounding is disabled for placement of the popup, allowing for subpixel positioning and smoother movement when the popup is translated.
     * @since 7.1.0
     */
    subpixelPositioning: {
      type: Boolean,
      default: !1
    },
    /**
     * Sets the popup's content to a string of text.
     */
    text: {
      type: String,
      required: !1
    }
  },
  setup(e, { slots: o, emit: r, expose: a }) {
    const t = y(O), n = y(Y, void 0), f = _(), s = new fe(e);
    n && n.value ? n.value.setPopup(s) : e.coordinates && t && s.setLngLat(e.coordinates).addTo(t.value), e.text && s.setText(e.text);
    function v(l) {
      const c = () => r(l);
      s.on(l, c), M(() => {
        s.off(l, c);
      });
    }
    return v("open"), v("close"), a({
      remove() {
        s.remove();
      }
    }), d(
      () => e.coordinates,
      (l) => {
        l && s.setLngLat(l);
      },
      { deep: !0 }
    ), d(
      () => e.text,
      (l) => s.setText(l || "")
    ), d(
      () => e.offset,
      (l) => s.setOffset(l)
    ), d(
      () => e.maxWidth,
      (l) => s.setMaxWidth(l)
    ), d(
      () => e.className,
      (l, c) => {
        c && s.removeClassName(c), l && s.addClassName(l);
      }
    ), d(
      () => e.subpixelPositioning,
      (l) => s.setSubpixelPositioning(l)
    ), D(() => {
      f.value && !e.text && s.setDOMContent(f.value);
    }), M(() => {
      s.remove();
    }), () => [
      T("div", { ref: f }, o.default ? o.default() : void 0)
    ];
  },
  /**
   * Slot for popup content
   * @slot default
   */
  render() {
    return null;
  }
});
class N {
  constructor() {
    q(this, "unmountHandlers", /* @__PURE__ */ new Map());
  }
  registerUnmountHandler(o, r) {
    this.unmountHandlers.set(o, r);
  }
  unregisterUnmountHandler(o) {
    this.unmountHandlers.delete(o);
  }
  unmount() {
    this.unmountHandlers.forEach((o) => o());
  }
}
const k = class k {
  static genSourceOpts(o) {
    const r = { ...o };
    for (const a of Object.keys(r))
      (r[a] === void 0 || a === "sourceId") && delete r[a];
    return r;
  }
  static getSourceRef(o, r) {
    const a = typeof r == "string", t = String(o) + (a ? r : "");
    let n = k.REFS.get(t);
    return n || (n = _(a ? null : void 0), k.REFS.set(t, n)), n;
  }
};
q(k, "REFS", /* @__PURE__ */ new Map());
let b = k;
function w(e, o, r) {
  const a = y(O), t = y(E);
  function n() {
    t.value && (a.value.addSource(o.sourceId, b.genSourceOpts(o)), e.value = a.value.getSource(o.sourceId));
  }
  return d(t, n, { immediate: !0 }), a.value.on("style.load", n), M(() => {
    t.value && (r.unmount(), a.value.removeSource(o.sourceId)), a.value.off("style.load", n);
  });
}
const we = g({
  name: "MglCanvasSource",
  props: {
    sourceId: {
      type: String,
      required: !0
    },
    coordinates: Array,
    animate: Boolean,
    canvas: [Object, String]
  },
  slots: Object,
  setup(e, { slots: o }) {
    const r = y(L), a = b.getSourceRef(r, e.sourceId), t = new N(), n = { ...e, type: "canvas" };
    return p(C, e.sourceId), p(x, t), w(a, n, t), d(
      P(e.coordinates) ? e.coordinates : () => e.coordinates,
      (f) => {
        var s;
        (s = a.value) == null || s.setCoordinates(f);
      },
      { immediate: !0 }
    ), () => [
      h("Canvas Source"),
      a.value && o.default ? o.default({}) : void 0
    ];
  }
}), Pe = g({
  name: "MglGeoJsonSource",
  props: {
    sourceId: {
      type: String,
      required: !0
    },
    data: {
      type: [Object, String],
      required: !0
    },
    maxzoom: Number,
    attribution: String,
    buffer: Number,
    tolerance: Number,
    cluster: Boolean,
    clusterRadius: Number,
    clusterMaxZoom: Number,
    clusterMinPoints: Number,
    clusterProperties: Object,
    lineMetrics: Boolean,
    generateId: Boolean,
    promoteId: [Object, String],
    filter: [Array, String, Object]
  },
  slots: Object,
  setup(e, { slots: o }) {
    const r = y(L), a = b.getSourceRef(r, e.sourceId), t = new N(), n = { ...e, type: "geojson" };
    return p(C, e.sourceId), p(x, t), w(a, n, t), d(
      P(e.data) ? e.data : () => e.data,
      (f) => {
        var s;
        (s = a.value) == null || s.setData(
          f || { type: "FeatureCollection", features: [] }
        );
      },
      { immediate: !0 }
    ), () => [
      h("GeoJSON Source"),
      a.value && o.default ? o.default({}) : void 0
    ];
  }
}), Ae = g({
  name: "MglImageSource",
  props: {
    sourceId: {
      type: String,
      required: !0
    },
    url: String,
    coordinates: Array
  },
  slots: Object,
  setup(e, { slots: o }) {
    const r = y(L), a = b.getSourceRef(r, e.sourceId), t = new N(), n = { ...e, type: "image" };
    return p(C, e.sourceId), p(x, t), w(a, n, t), d(
      P(e.coordinates) ? e.coordinates : () => e.coordinates,
      (f) => {
        var s;
        (s = a.value) == null || s.setCoordinates(f);
      },
      { immediate: !0 }
    ), () => [
      h("Image Source"),
      a.value && o.default ? o.default({}) : void 0
    ];
  }
}), ke = g({
  name: "MglRasterSource",
  props: {
    sourceId: {
      type: String,
      required: !0
    },
    url: String,
    tiles: Array,
    bounds: Array,
    minzoom: Number,
    maxzoom: Number,
    tileSize: Number,
    scheme: String,
    attribution: String,
    volatile: Boolean
  },
  slots: Object,
  setup(e, { slots: o }) {
    const r = y(L), a = b.getSourceRef(r, e.sourceId), t = new N(), n = { ...e, type: "raster" };
    return p(C, e.sourceId), p(x, t), w(a, n, t), () => [
      h("Raster Source"),
      a.value && o.default ? o.default({}) : void 0
    ];
  }
}), Te = g({
  name: "MglRasterDemSource",
  props: {
    sourceId: {
      type: String,
      required: !0
    },
    url: String,
    tiles: Array,
    bounds: Array,
    minzoom: Number,
    maxzoom: Number,
    tileSize: Number,
    attribution: String,
    encoding: String,
    volatile: Boolean,
    redFactor: Number,
    blueFactor: Number,
    greenFactor: Number,
    baseShift: Number
  },
  slots: Object,
  setup(e, { slots: o }) {
    const r = y(L), a = b.getSourceRef(r, e.sourceId), t = new N(), n = { ...e, type: "raster-dem" };
    return p(C, e.sourceId), p(x, t), w(a, n, t), () => [
      h("RasterDem Source"),
      a.value && o.default ? o.default({}) : void 0
    ];
  }
}), Ee = g({
  name: "MglVectorSource",
  props: {
    sourceId: {
      type: String,
      required: !0
    },
    url: String,
    tiles: Array,
    bounds: {
      type: Array,
      validator: function(e) {
        return e.length === 4;
      }
    },
    scheme: String,
    minzoom: Number,
    maxzoom: Number,
    attribution: String,
    promoteId: [Object, String],
    volatile: Boolean
  },
  slots: Object,
  setup(e, { slots: o }) {
    const r = y(L), a = b.getSourceRef(r, e.sourceId), t = new N(), n = { ...e, type: "vector" };
    return p(C, e.sourceId), p(x, t), w(a, n, t), d(
      P(e.tiles) ? e.tiles : () => e.tiles,
      (f) => {
        var s;
        (s = a.value) == null || s.setTiles(f || []);
      },
      { immediate: !0 }
    ), d(
      P(e.url) ? e.url : () => e.url,
      (f) => {
        var s;
        (s = a.value) == null || s.setUrl(f || "");
      },
      { immediate: !0 }
    ), () => [
      h("Vector Source"),
      a.value && o.default ? o.default({}) : void 0
    ];
  }
}), Fe = g({
  name: "MglVideoSource",
  props: {
    sourceId: {
      type: String,
      required: !0
    },
    urls: Array,
    coordinates: Array
  },
  slots: Object,
  setup(e, { slots: o }) {
    const r = y(L), a = b.getSourceRef(r, e.sourceId), t = new N(), n = { ...e, type: "video" };
    return p(C, e.sourceId), p(x, t), w(a, n, t), d(
      P(e.coordinates) ? e.coordinates : () => e.coordinates,
      (f) => {
        var s;
        (s = a.value) == null || s.setCoordinates(f);
      },
      { immediate: !0 }
    ), () => [
      h("Video Source"),
      a.value && o.default ? o.default({}) : void 0
    ];
  }
}), S = [
  "click",
  "dblclick",
  "mousedown",
  "mouseup",
  "mousemove",
  "mouseenter",
  "mouseleave",
  "mouseover",
  "mouseout",
  "contextmenu",
  "touchstart",
  "touchend",
  "touchcancel"
];
function B() {
  return {
    /**
     * The id of the layer
     */
    layerId: {
      type: String,
      required: !0
    },
    source: String,
    /**
     * Arbitrary properties useful to track with the layer, but do not influence rendering. Properties should be prefixed to avoid collisions, like 'maplibre:'.
     */
    metadata: [Object, Array, String, Number],
    /**
     * Layer to use from a vector tile source. Required for vector tile sources; prohibited for all other source types, including GeoJSON sources.
     */
    sourceLayer: String,
    /**
     * The minimum zoom level for the layer. At zoom levels less than the minzoom, the layer will be hidden.
     */
    minzoom: Number,
    /**
     * The maximum zoom level for the layer. At zoom levels equal to or greater than the maxzoom, the layer will be hidden.
     */
    maxzoom: Number,
    /**
     * A expression specifying conditions on source features. Only features that match the filter are displayed. Zoom expressions in filters are only evaluated at integer zoom levels. The feature-state expression is not supported in filter expressions.
     */
    filter: Object,
    /**
     * The ID of an existing layer to insert the new layer before, resulting in the new layer appearing visually beneath the existing layer. If this argument is not specified, the layer will be appended to the end of the layers array and appear visually above all other layers.
     */
    before: String,
    /**
     * Layout properties for the layer.
     * See https://maplibre.org/maplibre-style-spec/layers/
     */
    layout: Object,
    /**
     * Default paint properties for this layer.
     * See https://maplibre.org/maplibre-style-spec/layers/
     */
    paint: Object
  };
}
function Ze(e, o, r, a) {
  const t = {
    id: e,
    type: o,
    source: r.source || a,
    metadata: r.metadata,
    minzoom: r.minzoom,
    maxzoom: r.maxzoom,
    "source-layer": r.sourceLayer,
    filter: r.filter,
    paint: r.paint,
    layout: r.layout
  };
  for (const n of Object.keys(t))
    t[n] === void 0 && delete t[n];
  return t;
}
function qe(e, o, r) {
  if (r.props)
    for (const a of S) {
      const t = "on" + a.charAt(0).toUpperCase() + a.substr(1);
      r.props[t] && e.on(a, o, r.props[t]);
    }
}
function He(e, o, r) {
  if (r.props)
    for (const a of S) {
      const t = "on" + a.charAt(0).toUpperCase() + a.substr(1);
      r.props[t] && e.off(a, o, r.props[t]);
    }
}
function ee(e, o) {
  const r = y(O), a = y(E), t = y(x);
  function n() {
    a.value && (o && He(r.value, e, o.vnode), r.value.getLayer(e) && r.value.removeLayer(e));
  }
  t.registerUnmountHandler(e, n), M(() => {
    t.unregisterUnmountHandler(e), n();
  });
}
const We = g({
  name: "MglBackgroundLayer",
  props: {
    /**
     * The id of the layer
     */
    layerId: {
      type: String,
      required: !0
    },
    /**
     * Arbitrary properties useful to track with the layer, but do not influence rendering. Properties should be prefixed to avoid collisions, like 'maplibre:'.
     */
    metadata: [Object, Array, String, Number],
    /**
     * The minimum zoom level for the layer. At zoom levels less than the minzoom, the layer will be hidden.
     */
    minzoom: Number,
    /**
     * The maximum zoom level for the layer. At zoom levels equal to or greater than the maxzoom, the layer will be hidden.
     */
    maxzoom: Number,
    /**
     * The ID of an existing layer to insert the new layer before, resulting in the new layer appearing visually beneath the existing layer. If this argument is not specified, the layer will be appended to the end of the layers array and appear visually above all other layers.
     */
    before: String,
    /**
     * Layout properties for the layer.
     * See https://maplibre.org/maplibre-style-spec/layers/#background
     */
    layout: Object,
    /**
     * Default paint properties for this layer.
     * See https://maplibre.org/maplibre-style-spec/layers/#background
     */
    paint: Object
  },
  emits: [...S],
  setup(e) {
    const o = y(O), r = y(E);
    return ee(e.layerId), d(
      () => e.layout,
      (a) => {
        if (a)
          for (const [t, n] of Object.entries(a))
            o.value.setLayoutProperty(e.layerId, t, n);
      }
    ), d(
      () => e.paint,
      (a) => {
        if (a)
          for (const [t, n] of Object.entries(a))
            o.value.setPaintProperty(e.layerId, t, n);
      }
    ), d(
      r,
      (a) => {
        a && o.value.addLayer(
          {
            id: e.layerId,
            type: "background",
            metadata: e.metadata,
            minzoom: e.minzoom,
            maxzoom: e.maxzoom,
            layout: e.layout,
            paint: e.paint
          },
          e.before || void 0
        );
      },
      { immediate: !0 }
    ), () => h("Background Layer");
  }
});
function I(e, o) {
  const r = y(C);
  if (!r && !o.source) {
    re(
      `${e} Layer: layer must be used inside source tag or source prop must be set`
    );
    return;
  }
  const a = $(), t = y(O), n = y(E), f = y(L), s = b.getSourceRef(f, o.source || r);
  return ee(o.layerId, a), d(
    () => o.minzoom,
    () => t.value.setLayerZoomRange(
      o.layerId,
      o.minzoom || 0,
      o.maxzoom || 24
    )
  ), d(
    () => o.maxzoom,
    () => t.value.setLayerZoomRange(
      o.layerId,
      o.minzoom || 0,
      o.maxzoom || 24
    )
  ), d(
    () => o.layout,
    (v) => {
      if (v)
        for (const [l, c] of Object.entries(v))
          t.value.setLayoutProperty(o.layerId, l, c);
    }
  ), d(
    () => o.paint,
    (v) => {
      if (v)
        for (const [l, c] of Object.entries(v))
          t.value.setPaintProperty(o.layerId, l, c);
    }
  ), d(
    () => o.filter,
    (v) => t.value.setFilter(o.layerId, v)
  ), d(
    [n, s],
    ([v, l]) => {
      v && (l || l === void 0) && (t.value.addLayer(
        Ze(o.layerId, e, o, r),
        o.before || void 0
      ), qe(t.value, o.layerId, a.vnode));
    },
    { immediate: !0 }
  ), () => h(`${e} Layer`);
}
const Ue = g({
  name: "MglCircleLayer",
  props: { ...B() },
  emits: [...S],
  setup(e) {
    return I("circle", e);
  }
}), De = g({
  name: "MglFillLayer",
  props: { ...B() },
  emits: [...S],
  setup(e) {
    return I("fill", e);
  }
}), Ge = g({
  name: "MglFillExtrusionLayer",
  props: { ...B() },
  emits: [...S],
  setup(e) {
    return I("fill-extrusion", e);
  }
}), Ve = g({
  name: "MglHeatmapLayer",
  props: { ...B() },
  emits: [...S],
  setup(e) {
    return I("heatmap", e);
  }
}), Je = g({
  name: "MglHillshadeLayer",
  props: { ...B() },
  emits: [...S],
  setup(e) {
    return I("hillshade", e);
  }
}), $e = g({
  name: "MglLineLayer",
  props: { ...B() },
  emits: [...S],
  setup(e) {
    return I("line", e);
  }
}), Ke = g({
  name: "MglRasterLayer",
  props: { ...B() },
  emits: [...S],
  setup(e) {
    return I("raster", e);
  }
}), Ye = g({
  name: "MglSymbolLayer",
  props: { ...B() },
  emits: [...S],
  setup(e) {
    return I("symbol", e);
  }
}), Qe = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  MglAttributionControl: Le,
  MglBackgroundLayer: We,
  MglCanvasSource: we,
  MglCircleLayer: Ue,
  MglCustomControl: Oe,
  MglFillExtrusionLayer: Ge,
  MglFillLayer: De,
  MglFullscreenControl: xe,
  MglGeoJsonSource: Pe,
  MglGeolocateControl: ze,
  MglHeatmapLayer: Ve,
  MglHillshadeLayer: Je,
  MglImageSource: Ae,
  MglLineLayer: $e,
  MglLogoControl: _e,
  MglMap: he,
  MglMarker: je,
  MglNavigationControl: Be,
  MglPopup: Ne,
  MglRasterDemSource: Te,
  MglRasterLayer: Ke,
  MglRasterSource: ke,
  MglScaleControl: Re,
  MglSymbolLayer: Ye,
  MglVectorSource: Ee,
  MglVideoSource: Fe
}, Symbol.toStringTag, { value: "Module" })), at = function(o) {
  Object.entries(Qe).forEach(([r, a]) => {
    o.component(r, a);
  });
};
export {
  Le as MglAttributionControl,
  We as MglBackgroundLayer,
  we as MglCanvasSource,
  Ue as MglCircleLayer,
  Oe as MglCustomControl,
  m as MglDefaults,
  Ge as MglFillExtrusionLayer,
  De as MglFillLayer,
  xe as MglFullscreenControl,
  Pe as MglGeoJsonSource,
  ze as MglGeolocateControl,
  Ve as MglHeatmapLayer,
  Je as MglHillshadeLayer,
  Ae as MglImageSource,
  $e as MglLineLayer,
  _e as MglLogoControl,
  he as MglMap,
  je as MglMarker,
  Be as MglNavigationControl,
  Ne as MglPopup,
  Te as MglRasterDemSource,
  Ke as MglRasterLayer,
  ke as MglRasterSource,
  Re as MglScaleControl,
  Ye as MglSymbolLayer,
  Ee as MglVectorSource,
  Fe as MglVideoSource,
  F as Position,
  L as componentIdSymbol,
  at as default,
  ye as isInitialized,
  K as isInitializedSymbol,
  E as isLoadedSymbol,
  ge as map,
  O as mapSymbol,
  Y as markerSymbol,
  C as sourceIdSymbol,
  x as sourceLayerRegistry,
  j as useControl,
  ee as useDisposableLayer,
  ot as useMap,
  Me as usePositionWatcher,
  w as useSource
};
//# sourceMappingURL=vue-maplibre-gl.es.js.map
